// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useState, useRef, useEffect, useCallback } from 'react';
import { streamChat, fetchSessions, fetchSessionMessages } from '../services/chatService';
import { useShellStore } from '../stores/shellStore';
import { EMS_CHAT_AGENT_NAME } from '../utils/constants';
import type { ChatContextPanelEvent, ChatMessage, ChatRequestMessage, ChatSession } from '../types';

const WELCOME_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: "Hello! I'm your Helios AI assistant. How can I help you today?",
  timestamp: new Date(),
};

export interface UseChatReturn {
  messages: ChatMessage[];
  inputValue: string;
  setInputValue: (_value: string) => void;
  isThinking: boolean;
  sessionId: string | undefined;
  sessions: ChatSession[];
  loadingSessions: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (_open: boolean) => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  handleSendMessage: (_e: React.FormEvent | React.KeyboardEvent) => void;
  handleNewChat: () => void;
  handleSelectSession: (_sessionId: string) => Promise<void>;
}

const ANIMAL_KEYWORDS = ['giraffe', 'elephant', 'zebra', 'lion'] as const;

export function useChat(): UseChatReturn {
  const selectedSiteId = useShellStore(s => s.selectedSiteId);
  const openContextPanel = useShellStore(s => s.openContextPanel);
  const sidebarOpen = useShellStore(s => s.chatSidebarOpen);
  const setSidebarOpen = useShellStore(s => s.setChatSidebarOpen);
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (sidebarOpen) {
      setLoadingSessions(true);
      fetchSessions()
        .then(setSessions)
        .finally(() => setLoadingSessions(false));
    }
  }, [sidebarOpen]);

  const handleNewChat = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsThinking(false);
    setSessionId(undefined);
    setMessages([WELCOME_MESSAGE]);
    setSidebarOpen(false);
  }, [setSidebarOpen]);

  const handleSelectSession = useCallback(
    async (selectedSessionId: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsThinking(false);
      setSessionId(selectedSessionId);
      setSidebarOpen(false);

      const sessionMessages = await fetchSessionMessages(selectedSessionId);
      if (sessionMessages.length > 0) {
        setMessages(
          sessionMessages.map(m => ({
            role: m.role as 'user' | 'assistant',
            content: m.content,
            timestamp: m.created_at ? new Date(m.created_at) : new Date(),
          }))
        );
      }
    },
    [setSidebarOpen]
  );

  const handleSendMessage = useCallback(
    (e: React.FormEvent | React.KeyboardEvent) => {
      e.preventDefault();
      if (!inputValue.trim() || isThinking) return;

      const userMessage: ChatMessage = {
        role: 'user',
        content: inputValue,
        timestamp: new Date(),
      };

      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      setInputValue('');
      setIsThinking(true);

      // Keyword-triggered context panel for animal terms (dev/demo only)
      if (process.env.NODE_ENV === 'development') {
        const lowerInput = inputValue.toLowerCase();
        const matchedAnimal = ANIMAL_KEYWORDS.find(a => lowerInput.includes(a));
        if (matchedAnimal) {
          openContextPanel({ type: 'animal-detail', animal: matchedAnimal });
        }
      }

      const apiMessages: ChatRequestMessage[] = updatedMessages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, assistantMessage]);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      streamChat(
        {
          messages: apiMessages,
          current_page: window.location.href,
          site_id: selectedSiteId ?? undefined,
          session_id: sessionId,
          agent_or_workflow_name: EMS_CHAT_AGENT_NAME,
        },
        (content: string) => {
          setIsThinking(false);
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            updated[updated.length - 1] = {
              ...last,
              content: last.content + content,
            };
            return updated;
          });
        },
        (error: string) => {
          setIsThinking(false);
          setMessages(prev => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            updated[updated.length - 1] = {
              ...last,
              content: last.content || `Sorry, something went wrong: ${error}`,
            };
            return updated;
          });
        },
        () => {
          setIsThinking(false);
          abortControllerRef.current = null;
        },
        (newSessionId: string) => {
          setSessionId(newSessionId);
        },
        controller.signal,
        (panel: ChatContextPanelEvent) => {
          openContextPanel(panel);
        }
      );
    },
    [inputValue, isThinking, messages, sessionId, selectedSiteId, openContextPanel]
  );

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  return {
    messages,
    inputValue,
    setInputValue,
    isThinking,
    sessionId,
    sessions,
    loadingSessions,
    sidebarOpen,
    setSidebarOpen,
    messagesEndRef,
    handleSendMessage,
    handleNewChat,
    handleSelectSession,
  };
}
